import React, {
  createContext,
  useContext,
  useState,
  PropsWithChildren,
  useCallback,
  useMemo,
} from "react";
import Modal from "~/components/Modal/Modal";
import {
  SUCCESS_SCREEN,
  PAYMENT_STATUS,
  PaymentStatus,
} from "~/modules/checkout/constants";

interface ModalContextModalProps {
  title: string;
  okText?: string;
  closeText?: string;
  bodyText: string;
  onConfirm?: () => void;
}

type ModalContextType = {
  showModal: (config: ModalContextModalProps) => void;
  hideModal: () => void;
  modalProps: ModalContextModalProps | null;
  showAssetModal: boolean;
  setShowAssetModal: (x: boolean) => void;
};

const ModalContext = createContext<ModalContextType>({
  showModal: () => null,
  hideModal: () => null,
  modalProps: null,
  showAssetModal: false,
  setShowAssetModal: () => {},
});

export const ModalContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [modalProps, setModalProps] = useState<ModalContextModalProps | null>(
    null,
  );

  const [hidePromotionModal, setHidePromotionModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);

  const hideModal = useCallback(() => {
    setModalProps(null);
  }, []);

  const showModal = useCallback(
    (config: ModalContextModalProps) => {
      setModalProps({
        ...config,
        ...(config.onConfirm && {
          onConfirm: () => {
            config.onConfirm?.();
            hideModal();
          },
        }),
      });
    },
    [hideModal],
  );

  const value = useMemo(
    () => ({
      showModal,
      hideModal,
      modalProps,
      hidePromotionModal,
      setHidePromotionModal,
      showAssetModal,
      setShowAssetModal,
    }),
    [hideModal, hidePromotionModal, modalProps, showAssetModal, showModal],
  );

  return (
    <>
      {/* Don't update children if the context changes */}
      {useMemo(
        () => (
          <ModalContext.Provider value={value}>
            {children}
          </ModalContext.Provider>
        ),
        [value, children],
      )}

      {/* Modal */}
      {modalProps && (
        <Modal
          {...modalProps}
          visible={!!modalProps}
          onClose={() => setModalProps(null)}
        />
      )}
    </>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }

  // to stop eslint complaining without requiring the whole context in dependency arrays
  const showModal = context.showModal;

  const openServiceUnavailableModal = useCallback(() => {
    showModal({
      title: SUCCESS_SCREEN.STORE_BUSY_TITLE,
      bodyText: SUCCESS_SCREEN.BUSY_MESSAGE,
      okText: SUCCESS_SCREEN.OK_BUTTON,
    });
  }, [showModal]);

  const openPaymentStatusModal = useCallback(
    (status: PaymentStatus, orderId?: string, onConfirm?: () => void) => {
      let title = "";
      let bodyText = "";

      switch (status) {
        case PAYMENT_STATUS.SUCCESS:
          title = SUCCESS_SCREEN.TITLE;
          bodyText = orderId
            ? `${SUCCESS_SCREEN.ORDER_PLACED}\n${SUCCESS_SCREEN.ORDER_NUMBER} ${orderId}\n${SUCCESS_SCREEN.THANK_YOU}`
            : SUCCESS_SCREEN.ORDER_PLACED;
          break;
        case PAYMENT_STATUS.FAILED:
          title = SUCCESS_SCREEN.FAILED_TITLE;
          bodyText = SUCCESS_SCREEN.FAILED_MESSAGE;
          break;
        default:
          return;
      }

      showModal({
        title,
        bodyText,
        okText:
          status === PAYMENT_STATUS.FAILED
            ? SUCCESS_SCREEN.RETRY_BUTTON
            : SUCCESS_SCREEN.OK_BUTTON,
        onConfirm,
      });
    },
    [showModal],
  );

  const openCustomModal = useCallback(
    (config: ModalContextModalProps) => {
      showModal(config);
    },
    [showModal],
  );

  return {
    ...context,
    openServiceUnavailableModal,
    openPaymentStatusModal,
    openCustomModal,
  };
};

export default ModalContext;
